/**
 * User Service
 *
 * API calls for managing site users (frontline workers)
 * Uses path-based multi-tenancy: /api/orgs/{orgId}/users
 */
import apiClient from './apiClient';

// Placeholder org ID - will come from auth context after login
const DEFAULT_ORG_ID = 1;

/**
 * Maps API response object from the new API schema (userId) back to
 * the internal UI field name (employeeId) so UI components don't need changes.
 */
function mapUserResponse(obj) {
  if (!obj) return obj;
  if (obj.userId !== undefined && obj.employeeId === undefined) {
    const { userId, ...rest } = obj;
    return { employeeId: userId, ...rest };
  }
  return obj;
}

/** Maps a paged/list response — normalizes each item in content/items arrays. */
function mapPagedResponse(data) {
  if (!data) return data;
  if (Array.isArray(data.content)) {
    return { ...data, content: data.content.map(mapUserResponse) };
  }
  if (Array.isArray(data.items)) {
    return { ...data, items: data.items.map(mapUserResponse) };
  }
  if (Array.isArray(data)) {
    return data.map(mapUserResponse);
  }
  return data;
}

const userService = {
  // =============================================================================
  // USER CRUD
  // =============================================================================

  /**
   * List users for an org with pagination
   * @param {number} orgId - Organization ID
   * @param {number} page - Page number (0-indexed)
   * @param {number} size - Page size
   * @param {number} siteId - Optional site ID
   * @returns {Promise<Object>} Paged response with users
   */
  listUsers: async (orgId = DEFAULT_ORG_ID, page = 0, size = 50, siteId) => {
    const params = { page, size };
    if (siteId) {
      params.siteId = siteId;
    }
    const response = await apiClient.get(`/api/orgs/${orgId}/users`, {
      params
    });
    return mapPagedResponse(response.data);
  },

  /**
   * Get user details by ID
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} User details
   */
  getUser: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}`);
    return mapUserResponse(response.data);
  },

  /**
   * Create a new user
   * @param {Object} userData - User creation data
   * @returns {Promise<Object>} Created user with employeeId
   */
  createUser: async (userData) => {
    const orgId = userData.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.post(`/api/orgs/${orgId}/users`, {
      orgId,
      siteId: userData.siteId,
      siteIds: userData.siteIds,
      roleId: userData.roleId,
      altaUserId: userData.altaUserId,
      customerEmployeeId: userData.employeeId || userData.customerEmployeeId,
      firstName: userData.firstName,
      middleName: userData.middleName,
      lastName: userData.lastName,
      preferredFirstName: userData.preferredName || userData.preferredFirstName,
      preferredLastName: userData.preferredLastName,
      namePronunciation: userData.namePronunciation,
      email: userData.email,
      phone: userData.phone || userData.phoneNumber,
      defaultLanguage: userData.language || userData.defaultLanguage || 'en-US',
      homeTalkGroupId: userData.homeTalkGroupId,
      emergencyTalkGroupId: userData.emergencyTalkGroupId,
      defaultPolicyId: userData.defaultPolicyId,
      mobileAppPermissions: userData.mobileAppPermissions,
      mobileAppCommsOnly: userData.mobileAppCommsOnly,
      mobileAppCommsVideo: userData.mobileAppCommsVideo,
      status: userData.status?.toUpperCase(),
      hireDate: userData.hireDate,
      startDate: userData.startDate,
      endDate: userData.endDate,
      timezone: userData.timezone,
      description: userData.description,
      locationSharingEnabled: userData.locationSharingEnabled,
      pairingPin: userData.pairingPin,
    });
    return mapUserResponse(response.data);
  },

  /**
   * Update user details
   * @param {number} employeeId - Employee ID
   * @param {Object} userData - Fields to update
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (employeeId, userData, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.patch(`/api/orgs/${orgId}/users/${employeeId}`, {
      roleId: userData.roleId,
      siteId: userData.siteId,
      siteIds: userData.siteIds,
      altaUserId: userData.altaUserId,
      firstName: userData.firstName,
      middleName: userData.middleName,
      lastName: userData.lastName,
      preferredFirstName: userData.preferredName || userData.preferredFirstName,
      preferredLastName: userData.preferredLastName,
      namePronunciation: userData.namePronunciation,
      email: userData.email,
      phone: userData.phone || userData.phoneNumber,
      defaultLanguage: userData.language || userData.defaultLanguage,
      homeTalkGroupId: userData.homeTalkGroupId,
      emergencyTalkGroupId: userData.emergencyTalkGroupId,
      defaultPolicyId: userData.defaultPolicyId,
      mobileAppPermissions: userData.mobileAppPermissions,
      mobileAppCommsOnly: userData.mobileAppCommsOnly,
      mobileAppCommsVideo: userData.mobileAppCommsVideo,
      status: userData.status?.toUpperCase(),
      hireDate: userData.hireDate,
      startDate: userData.startDate,
      endDate: userData.endDate,
      timezone: userData.timezone,
      description: userData.description,
      terminationDate: userData.terminationDate,
      locationSharingEnabled: userData.locationSharingEnabled,
      usabilityPreferences: userData.usabilityPreferences,
      pairingPin: userData.pairingPin,
    });
    return mapUserResponse(response.data);
  },

  /**
   * Update user status
   * @param {number} employeeId - Employee ID
   * @param {string} status - New status (ACTIVE, PAUSED, TERMINATED)
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} Updated user
   */
  updateUserStatus: async (employeeId, status, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.patch(`/api/orgs/${orgId}/users/${employeeId}`, {
      status: status.toUpperCase()
    });
    return mapUserResponse(response.data);
  },

  /**
   * Delete a user
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteUser: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}`);
  },

  // =============================================================================
  // USER SEARCH
  // =============================================================================

  /**
   * Search users with various criteria
   * @param {Object} criteria - Search criteria
   * @param {number} page - Page number
   * @param {number} size - Page size
   * @returns {Promise<Object>} Paged response with search results
   */
  searchUsers: async (criteria, page = 0, size = 50) => {
    const orgId = criteria.orgId || DEFAULT_ORG_ID;
    const response = await apiClient.get(`/api/orgs/${orgId}/users`, {
      params: { page, size }
    });
    return mapPagedResponse(response.data);
  },

  // =============================================================================
  // PAIRING PIN
  // =============================================================================

  /**
   * Set pairing PIN for a user
   * @param {number} employeeId - Employee ID
   * @param {string} pin - 4-8 digit PIN
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  setPairingPin: async (employeeId, pin, orgId = DEFAULT_ORG_ID) => {
    await apiClient.put(`/api/orgs/${orgId}/users/${employeeId}/pairing-pin`, { pin });
  },

  /**
   * Verify pairing PIN for a user
   * @param {number} employeeId - Employee ID
   * @param {string} pin - PIN to verify
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} { valid: boolean }
   */
  verifyPairingPin: async (employeeId, pin, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/users/${employeeId}/verify-pin`, { pin });
    return response.data;
  },

  // =============================================================================
  // BULK IMPORT (async — submit CSV, poll for completion)
  // =============================================================================

  /**
   * Bulk import users from CSV file (async)
   * Submits CSV, receives job ID, polls until completion.
   * @param {number} orgId - Organization ID
   * @param {File} file - CSV file (max 700KB, max 3000 rows)
   * @param {Object} options - Additional options (siteId, defaultRoleId)
   * @param {Function} onProgress - Optional callback(job) for progress updates
   * @returns {Promise<Object>} Final import results {successes, failures, jobId}
   */
  bulkImportUsers: async (orgId, file, options = {}, onProgress) => {
    const resolvedOrgId = orgId || DEFAULT_ORG_ID;

    // Client-side file size validation (700 KB)
    const MAX_FILE_SIZE = 700 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('CSV file exceeds maximum allowed size of 700 KB');
    }

    const formData = new FormData();
    formData.append('file', file);

    const params = {};
    if (options.siteId) params.siteId = options.siteId;
    if (options.defaultRoleId) params.defaultRoleId = options.defaultRoleId;

    // Submit — expect HTTP 202 Accepted
    const submitResponse = await apiClient.post(
      `/api/orgs/${resolvedOrgId}/users/bulk`,
      formData,
      { params }
    );

    const { jobId, statusUrl } = submitResponse.data;
    if (!jobId) {
      throw new Error('No job ID returned from bulk import');
    }

    // Poll for completion
    const pollUrl = statusUrl || `/api/orgs/${resolvedOrgId}/users/bulk-jobs/${jobId}`;
    const POLL_INTERVAL_MS = 2000;
    const MAX_POLL_ATTEMPTS = 150; // 5 minutes max

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

      const pollResponse = await apiClient.get(pollUrl);
      const job = pollResponse.data;

      if (onProgress) {
        onProgress(job);
      }

      if (job.status === 'COMPLETED' || job.status === 'FAILED') {
        if (job.status === 'FAILED') {
          throw new Error(job.errorMessage || 'Bulk user import job failed');
        }
        return {
          successes: job.successes || [],
          failures: job.failures || [],
          jobId: job.jobId
        };
      }
    }

    throw new Error('Bulk user import timed out. Check job status: ' + jobId);
  },

  // =============================================================================
  // SKILLS
  // =============================================================================

  listSkills: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/skills`);
    return response.data;
  },

  addSkill: async (employeeId, skill, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/users/${employeeId}/skills`, skill);
    return response.data;
  },

  deleteSkill: async (employeeId, skillId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}/skills/${skillId}`);
  },

  // =============================================================================
  // DEVICE RESTRICTIONS
  // =============================================================================

  listDeviceRestrictions: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/device-restrictions`);
    return response.data;
  },

  addDeviceRestriction: async (employeeId, restriction, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/users/${employeeId}/device-restrictions`, restriction);
    return response.data;
  },

  deleteDeviceRestriction: async (employeeId, restrictionId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}/device-restrictions/${restrictionId}`);
  },

  // =============================================================================
  // SPOKEN LANGUAGES
  // =============================================================================

  listSpokenLanguages: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/spoken-languages`);
    return response.data;
  },

  addSpokenLanguage: async (employeeId, language, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/users/${employeeId}/spoken-languages`, language);
    return response.data;
  },

  deleteSpokenLanguage: async (employeeId, spokenLanguageId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}/spoken-languages/${spokenLanguageId}`);
  },

  // =============================================================================
  // USER TALK GROUPS
  // =============================================================================

  /**
   * Assign talk groups to a user (replaces all existing assignments)
   * @param {number} employeeId - Employee ID
   * @param {Array<number>} talkGroupIds - List of talk group IDs
   * @param {number|null} primaryTalkGroupId - Optional primary talk group ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} Updated list of user talk groups
   */
  assignTalkGroups: async (employeeId, talkGroupIds, primaryTalkGroupId = null, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/users/${employeeId}/talk-groups`, {
      talkGroupIds,
      primaryTalkGroupId
    });
    return response.data;
  },

  /**
   * List talk groups assigned to a user
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of talk group assignments
   */
  getEmployeeTalkGroups: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/talk-groups`);
    return response.data;
  },

  /**
   * Remove a single talk group from a user
   * @param {number} employeeId - Employee ID
   * @param {number} talkGroupId - Talk group ID to remove
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  removeEmployeeTalkGroup: async (employeeId, talkGroupId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}/talk-groups/${talkGroupId}`);
  },

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Get user statistics for dashboard
   * @param {number} orgId - Organization ID
   * @returns {Promise<Object>} User statistics
   */
  getUserStats: async (orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users`, {
      params: { page: 0, size: 1 }
    });
    return {
      totalUsers: response.data.totalElements || 0,
    };
  },

  /**
   * Validate a frontline user's pairing PIN by ID.
   * Backend: POST /api/orgs/{orgId}/users/verify-pin
   * Request: { orgId, siteId, userId, pin }
   * Response: { valid, reason, orgId, siteId, userId }
   */
  verifyPin: async ({ orgId, siteId, employeeId, pin }) => {
    const response = await apiClient.post(`/api/orgs/${orgId}/users/verify-pin`, {
      orgId: Number(orgId),
      siteId: Number(siteId),
      userId: Number(employeeId),
      pin,
    });
    return mapUserResponse(response.data);
  },

  // =============================================================================
  // USER DEPARTMENTS/TEAMS
  // =============================================================================

  /**
   * Assign departments to a user (replaces all existing assignments)
   * @param {number} employeeId - Employee ID
   * @param {Array<number>} departmentIds - List of department IDs
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} Updated list of department IDs assigned to user
   */
  assignDepartments: async (employeeId, departmentIds, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.put(`/api/orgs/${orgId}/users/${employeeId}/departments`, {
      departmentIds
    });
    return response.data;
  },

  /**
   * Get departments assigned to a user
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Array>} List of department IDs
   */
  getEmployeeDepartments: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/departments`);
    return response.data;
  },

  // =============================================================================
  // PROFILE IMAGE
  // =============================================================================

  /**
   * Upload profile image for a user (replaces existing if any)
   * @param {number} employeeId - Employee ID
   * @param {File} file - Image file (max 512 KB, JPEG/PNG/GIF/WebP)
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  uploadProfileImage: async (employeeId, file, orgId = DEFAULT_ORG_ID) => {
    const formData = new FormData();
    formData.append('file', file);
    await apiClient.post(
      `/api/orgs/${orgId}/users/${employeeId}/profile-image`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },

  /**
   * Get profile image URL for a user (for use in img src)
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {string} URL that returns the image bytes
   */
  getProfileImageUrl: (employeeId, orgId = DEFAULT_ORG_ID) => {
    return `/api/orgs/${orgId}/users/${employeeId}/profile-image`;
  },

  /**
   * Fetch profile image bytes for a user.
   * Uses apiClient so auth headers/interceptors are applied.
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<Blob>} Profile image blob
   */
  getProfileImageBlob: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    const response = await apiClient.get(`/api/orgs/${orgId}/users/${employeeId}/profile-image`, {
      responseType: 'blob'
    });
    return response.data;
  },

  /**
   * Delete profile image for a user
   * @param {number} employeeId - Employee ID
   * @param {number} orgId - Organization ID
   * @returns {Promise<void>}
   */
  deleteProfileImage: async (employeeId, orgId = DEFAULT_ORG_ID) => {
    await apiClient.delete(`/api/orgs/${orgId}/users/${employeeId}/profile-image`);
  },
};

export default userService;






